import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useParentDigestsList, useParentDigestResend } from '../../api/hooks';
import { SchoolApi } from '../../api/client';
import {
  PanelCard, Badge, Button, Spinner, ErrorPanel, EmptyState, PagedFooter, SortableTh, PageHeader, KpiCard, Tabs, Chip, Track, ConfirmDialog, Table, Td, Segmented, PillTabs,
} from '../../components/ui';
import { FaUsers } from '../../components/ui/icons';
import DigestPreview, { type PreviewChannel } from './DigestPreview';
import { audienceLabel, audienceRows, classLine, nextSunday, sectionName, shortDay, shortName, weekRange } from './digestHelpers';
import type { ParentDigestRow, ParentDigestStatus } from '../../api/types';
import { useSortableTable, SortColumn } from '../../lib/useSortableTable';
import { prettifySlug } from '../../lib/caseStatus';
import usePagination from '../../lib/usePagination';
import { timeOf } from '../../lib/dates';

// Snap an arbitrary picked date to the Monday of its week -- the backend
// filters `week` as a literal date match, so picking e.g. a Wednesday must
// resolve to that week's Monday for the digest rows to be found.
//
// Done entirely in UTC (Date.UTC / getUTCDay / setUTCDate / toISOString,
// which is always UTC) rather than mixing local-time parsing with a UTC
// serialization -- that mix is off by one day for any viewer in a
// positive UTC offset (e.g. Africa/Addis_Ababa, UTC+3), since local
// midnight is the previous UTC day.
function mondayOf(dateStr: string): string {
  const [y, m, day] = dateStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, day));
  const dow = d.getUTCDay(); // 0 = Sunday .. 6 = Saturday
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diffToMonday);
  return d.toISOString().slice(0, 10);
}

const STATUS_TONE: Record<ParentDigestStatus, 'neutral' | 'success' | 'danger' | 'warning'> = {
  not_generated: 'neutral',
  pending: 'warning',
  sent: 'success',
  opened: 'success',
  failed: 'danger',
  opted_out: 'neutral',
};

const statusTone = (s: string) => (STATUS_TONE as Record<string, 'neutral' | 'success' | 'danger' | 'warning'>)[s] ?? 'neutral';
const statusLabel = (s: string) => (STATUS_LABEL as Record<string, string>)[s] ?? prettifySlug(s);

const STATUS_LABEL: Record<ParentDigestStatus, string> = {
  not_generated: 'Not generated',
  pending: 'Pending',
  sent: 'Sent',
  opened: 'Opened',
  failed: 'Failed',
  opted_out: 'Opted out',
};

type TabKey = 'all' | 'sent' | 'failed' | 'pending' | 'opted_out';
const TAB_KEYS: TabKey[] = ['all', 'sent', 'failed', 'pending', 'opted_out'];
const TAB_LABEL: Record<TabKey, string> = {
  all: 'All', sent: 'Sent', failed: 'Failed', pending: 'Pending', opted_out: 'Opted out',
};

const FIELD_WRAP = 'flex min-h-[38px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-accent';
const STEP_LABEL = 'mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const FIELD_INPUT = 'min-w-0 border-0 bg-transparent py-2 text-sm text-ink outline-none';
const CHANNEL_ON = 'bg-mist text-ink';
const CHANNEL_OFF = 'bg-note text-muted line-through';

const DIGEST_COLUMNS: SortColumn<ParentDigestRow>[] = [
  { key: 'student', accessor: (r) => r.student_name },
  { key: 'parent', accessor: (r) => r.parent_name || r.parent_email || undefined },
  {
    key: 'channels',
    accessor: (r) => [r.parent_email ? 'Email' : null, r.telegram_linked ? 'Telegram' : null].filter(Boolean).join(', ') || undefined,
  },
  { key: 'status', accessor: (r) => statusLabel(r.status) },
  { key: 'sent_at', accessor: (r) => r.sent_at, type: 'date' },
];


// "Sun 11 Oct, 6:02 PM" in the viewer's local time.
function formatSentAt(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    .formatToParts(new Date(iso));
  const get = (t: string) => (parts.find((p) => p.type === t)?.value ?? '').replace(/^Sept$/, 'Sep');
  return `${get('weekday')} ${get('day')} ${get('month')}, ${timeOf(iso)}`;
}

export default function ParentDigestPage() {
  // '' means "no week chosen yet" -- the backend then defaults to the most
  // recent week that has digests, which the (non-Monday-aware) literal-date
  // filter would otherwise miss on any day that isn't itself a digest week.
  const [week, setWeek] = useState<string>('');
  const { data, isLoading, isError, refetch } = useParentDigestsList(week || undefined);
  const resend = useParentDigestResend(week || undefined);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const [confirmAll, setConfirmAll] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [grade, setGrade] = useState<string | null>(null);
  const [section, setSection] = useState<string | null>(null);
  const [previewChannel, setPreviewChannel] = useState<PreviewChannel>('telegram');
  const [previewId, setPreviewId] = useState<number | null>(null);
  const gradesQ = useQuery({ queryKey: ['analytics.grades'], queryFn: () => SchoolApi.analyticsGrades() });

  const rawRows = useMemo(() => data?.results ?? [], [data]);

  // Once the backend's default week resolves, hydrate the date input from
  // it so the picker reflects what's actually shown (purely cosmetic --
  // doesn't change which week is fetched, since it only fires when `week`
  // is still ''). Uses the raw (unsorted) rows -- any row's week_start works,
  // since they all share the same resolved week.
  useEffect(() => {
    if (!week && rawRows.length > 0) {
      setWeek(rawRows[0].week_start);
    }
  }, [week, rawRows]);

  // An opened digest was sent, so it counts under the Sent tab.
  const inTab = (k: TabKey, r: ParentDigestRow) => (k === 'all' ? true : k === 'sent' ? r.status === 'sent' || r.status === 'opened' : r.status === k);
  const count = (k: TabKey) => rawRows.filter((r) => inTab(k, r)).length;
  const shown = rawRows.filter((r) => inTab(tab, r));
  const { rows, sort, toggle } = useSortableTable(shown, DIGEST_COLUMNS, { key: 'student', dir: 'asc' });
  const pg = usePagination(rows, 10, `${week}|${tab}|${sort?.key}|${sort?.dir}`);

  const handleResend = async (id: number) => {
    setBusyId(id);
    try {
      await resend.mutateAsync(id);
    } finally {
      setBusyId(null);
    }
  };

  const failedIds = rawRows.filter((r) => r.status === 'failed' && r.id != null).map((r) => r.id as number);

  const handleResendAll = async () => {
    setBulkBusy(true);
    let ok = 0;
    let err = 0;
    for (const id of failedIds) {
      try {
        await resend.mutateAsync(id);
        ok += 1;
      } catch {
        err += 1;
      }
    }
    setBulkBusy(false);
    setConfirmAll(false);
    setBulkResult(`${ok} resent · ${err} failed`);
  };

  const summary = data?.summary;
  const weekStart = week || rawRows[0]?.week_start || '';
  const weekLabel = weekStart ? shortDay(weekStart) : '';
  const lastSent = rawRows.reduce<string | null>((max, r) => (r.sent_at && (!max || r.sent_at > max) ? r.sent_at : max), null);
  const pending = count('pending');
  const sentPct = summary && summary.consented > 0 ? Math.round((summary.sent * 100) / summary.consented) : null;
  const historySub = [weekLabel && `Week of ${weekLabel}`, lastSent && `sent ${formatSentAt(lastSent)}`].filter(Boolean).join(' · ');

  // Total students: every grade's headcount, for "of 438 students · 42%".
  const totalStudents = (gradesQ.data?.grades ?? []).reduce((n, g) => n + g.student_count, 0);
  const consentedPct = summary && totalStudents > 0 ? Math.round((summary.consented * 100) / totalStudents) : null;

  // Compose card: the audience, the week and the channels, all read from this week's rows.
  const gradeNames = useMemo(() => Array.from(new Set(rawRows.map((r) => r.grade_name).filter((g): g is string => !!g))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [rawRows]);
  const sectionNames = useMemo(
    () => Array.from(new Set(audienceRows(rawRows, grade, null).map((r) => sectionName(r)).filter((x): x is string => !!x))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [rawRows, grade],
  );
  const audience = useMemo(() => audienceRows(rawRows, grade, section), [rawRows, grade, section]);
  const telegramN = rawRows.filter((r) => r.telegram_linked).length;
  const emailN = rawRows.filter((r) => r.parent_email).length;
  // Only an explicitly picked student is fetched, so opening the page reads no student's parent record.
  const previewRow = audience.find((r) => r.student_id === previewId) ?? null;
  const nextSend = shortDay(nextSunday());

  return (
    <>
      <PageHeader
        eyebrow={weekLabel ? `Workflow · week of ${weekLabel}` : 'Workflow'}
        title="Parent digest"
        subtitle={
          <>
            <span>Digests send automatically every Sunday.</span>
            {summary && <span>{' '}{summary.consented} families consented; next send {nextSend}.</span>}
          </>
        }
      />

      {summary && (
        <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-4">
          <KpiCard label="Consented" size="lg" value={summary.consented}
            sub={consentedPct != null ? <p>of {totalStudents} students · {consentedPct}%</p> : undefined} />
          <KpiCard label="Sent" size="lg" value={summary.sent}
            chip={sentPct != null ? <Chip tone="up">{sentPct}%</Chip> : undefined}
            track={sentPct != null ? <Track className="mt-0" value={sentPct} tone="slate" label="Sent share of consented" /> : undefined} />
          <KpiCard label="Failed" size="lg" tone={summary.failed > 0 ? 'danger' : 'default'} value={summary.failed}
            chip={summary.failed > 0 ? <Chip tone="down">resend</Chip> : undefined}
            sub={<p>bounced email or blocked bot</p>} />
          <KpiCard label="Opted out" size="lg" value={summary.opted_out} sub={<p>{pending} more still pending</p>} />
        </div>
      )}

      <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-12">
        <PanelCard title="This week's digest" subtitle="Each family gets their own child's numbers. Only consented parents receive it."
          className="min-[821px]:col-span-6 min-[1181px]:col-span-7">
          <div className="divide-y divide-hairline">
            {gradeNames.length > 0 && (
              <div className="flex gap-3.5 py-[18px] first:pt-0">
                <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-mist font-mono text-xs font-semibold text-ink">1</span>
                <div className="min-w-0 flex-1">
                  <span className={STEP_LABEL}>Audience</span>
                  <div className="flex flex-col gap-2.5">
                    <PillTabs label="Grade" dense height={36} weight="semibold" scroll value={grade ?? ''}
                      onChange={(k) => { setGrade(k || null); setSection(null); }}
                      options={[{ key: '', label: 'All grades' }, ...gradeNames.map((g) => ({ key: g, label: g }))]} />
                    {grade && sectionNames.length > 0 && (
                      <PillTabs label="Sections" dense height={36} weight="semibold" scroll value={section ?? ''}
                        onChange={(k) => setSection(k || null)}
                        options={[{ key: '', label: 'All sections' }, ...sectionNames.map((n) => ({ key: n, label: `Section ${n}` }))]} />
                    )}
                  </div>
                  <p className="mt-2.5 text-[13px] text-muted">{audience.length} consented {audience.length === 1 ? 'family' : 'families'} in {audienceLabel(grade, section)}</p>
                </div>
              </div>
            )}
            <div className={`flex gap-3.5 py-[18px] ${gradeNames.length === 0 ? 'pt-0' : ''}`}>
              <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-mist font-mono text-xs font-semibold text-ink">{gradeNames.length > 0 ? 2 : 1}</span>
              <div className="min-w-0 flex-1">
                <span className={STEP_LABEL}>Week</span>
                <div className="flex flex-wrap items-center gap-2.5">
                  <label className={FIELD_WRAP} htmlFor="parent-digest-week">
                    <span className="sr-only">Week</span>
                    <input
                      id="parent-digest-week"
                      type="date"
                      value={week}
                      placeholder="Most recent week"
                      onChange={(e) => setWeek(e.target.value ? mondayOf(e.target.value) : '')}
                      className={FIELD_INPUT}
                    />
                  </label>
                  {weekStart && <span className="text-[13px] text-muted">{weekRange(weekStart)} · any date snaps to its Monday</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-3.5 pb-0 pt-[18px]">
              <span className="grid h-7 w-7 flex-none place-items-center rounded-full bg-mist font-mono text-xs font-semibold text-ink">{gradeNames.length > 0 ? 3 : 2}</span>
              <div className="min-w-0 flex-1">
                <span className={STEP_LABEL}>Channels</span>
                <p className="text-sm">Sent by <strong>email</strong> and the <strong>YADA Telegram bot</strong> wherever the parent has linked it.{rawRows.length > 0 && ` ${telegramN} linked Telegram, ${emailN} have an email.`}</p>
              </div>
            </div>
          </div>
        </PanelCard>

        <PanelCard title="Preview" className="min-[821px]:col-span-6 min-[1181px]:col-span-5"
          actions={<Segmented label="Preview channel" value={previewChannel} onChange={setPreviewChannel}
            options={[{ key: 'telegram', label: 'Telegram' }, { key: 'email', label: 'Email' }]} />}>
          {audience.length > 0 ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-[13px] text-muted">Preview for</span>
                <PillTabs label="Preview student" dense height={36} weight="semibold" scroll value={previewRow ? String(previewRow.student_id) : ''}
                  onChange={(k) => setPreviewId(Number(k))}
                  options={audience.map((r) => ({ key: String(r.student_id), label: shortName(r.student_name) }))} />
              </div>
              {previewRow ? <DigestPreview studentId={previewRow.student_id} name={previewRow.student_name} channel={previewChannel} parentEmail={previewRow.parent_email} /> : <p className="py-6 text-center text-sm text-muted">Pick a student to see the digest their parent receives.</p>}
            </>
          ) : (
            <p className="py-6 text-center text-sm text-muted">Pick a week with consented parents to preview its digest.</p>
          )}
        </PanelCard>
      </div>

      <PanelCard
        title="Send history"
        subtitle={historySub || undefined}
        actions={
          <Button variant="ghost" size="xs" className="!text-[13px]" icon={<RefreshCw size={18} strokeWidth={1.8} aria-hidden="true" />}
            disabled={failedIds.length === 0 || bulkBusy} onClick={() => setConfirmAll(true)}>
            Resend all failed
          </Button>
        }
      >
        {bulkResult && <p role="status" className="mb-3 text-sm text-muted-2">{bulkResult}</p>}
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : isError ? (
          <ErrorPanel onRetry={() => refetch()} />
        ) : rawRows.length === 0 ? (
          <EmptyState icon={<FaUsers />} title="No consented parents"
            body="No students have parent digest consent enabled for this week." />
        ) : (
          <>
            <Tabs thick className="!mb-3.5" items={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABEL[k], count: count(k) }))} value={tab} onChange={setTab} label="Digest status" />
            {rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">No digests with this status.</p>
            ) : (
              <Table minWidth={820} className="text-sm">
                <thead>
                  <tr className="[&_th]:border-b [&_th]:border-line">
                    <SortableTh columnKey="student" sort={sort} onToggle={toggle}>Student</SortableTh>
                    <SortableTh columnKey="parent" sort={sort} onToggle={toggle}>Parent</SortableTh>
                    <SortableTh columnKey="channels" sort={sort} onToggle={toggle}>Channels</SortableTh>
                    <SortableTh columnKey="status" sort={sort} onToggle={toggle}>Status</SortableTh>
                    <SortableTh columnKey="sent_at" sort={sort} onToggle={toggle}>Sent at</SortableTh>
                    <th className="border-b border-line px-2.5 pb-2.5"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {pg.pageItems.map((row) => (
                    <DigestRow
                      key={row.id ?? `virtual-${row.student_id}`}
                      row={row}
                      busy={busyId === row.id}
                      onResend={() => row.id != null && handleResend(row.id)}
                    />
                  ))}
                </tbody>
              </Table>
            )}
            {rows.length > 0 && (
              <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="mt-4" />
            )}
          </>
        )}
      </PanelCard>

      <ConfirmDialog
        isOpen={confirmAll}
        title={`Resend ${failedIds.length} failed ${failedIds.length === 1 ? 'digest' : 'digests'}?`}
        body="Each failed digest is sent again to its parent."
        confirmLabel="Resend all"
        danger={false}
        busy={bulkBusy}
        onConfirm={handleResendAll}
        onCancel={() => setConfirmAll(false)}
      />
    </>
  );
}

function ChannelPill({ on, children }: { on: boolean; children: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-[3px] text-[11.5px] font-semibold ${on ? CHANNEL_ON : CHANNEL_OFF}`}>
      {children}
    </span>
  );
}

function DigestRow({ row, busy, onResend }: {
  row: ParentDigestRow; busy: boolean; onResend: () => void;
}) {
  const line = classLine(row);
  return (
    <tr>
      <Td>
        <Link to={`/students/profile/${row.student_id}`} className="font-semibold text-ink hover:underline">{row.student_name}</Link>
        {line && <span className="block text-xs text-muted">{line}</span>}
      </Td>
      <Td>
        <div className="text-ink">{row.parent_name || '—'}</div>
        <div className="text-xs text-muted">{row.parent_email || 'No email'}</div>
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <ChannelPill on={!!row.parent_email}>Email</ChannelPill>
          <ChannelPill on={row.telegram_linked}>Telegram</ChannelPill>
        </div>
      </Td>
      <Td>
        <Badge tone={statusTone(row.status)} className={row.status === 'opted_out' || row.status === 'not_generated' ? '!text-muted-2' : ''}>{statusLabel(row.status)}</Badge>
      </Td>
      <Td className="whitespace-nowrap font-mono text-[13px] text-muted">{row.sent_at ? formatSentAt(row.sent_at) : '—'}</Td>
      <Td align="right">
        {row.status === 'opted_out' ? (
          <span className="text-muted">—</span>
        ) : (
          <Button variant="ghost" size="xs" className="!text-[13px]" disabled={row.id == null || busy} loading={busy} onClick={onResend}>
            Resend
          </Button>
        )}
      </Td>
    </tr>
  );
}
